// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { addParametersToURL, isDefined, getRandomInteger, throttle } from '@utilities';

// Import style for web component
const styles = require( './web-component-style.scss' );

// eslint-disable-next-line require-jsdoc
@customElement( 'lfm-faq-item' )
export class FAQItem extends LitElement {
    // Define style
    static styles = css`${ unsafeCSS( styles ) }`;

    // Properties
	@property({ type: Number, attribute: 'faq-id' }) faqID;
	@property({ type: Boolean }) open = false;

	@property({ type: String, attribute: false }) editURL;
	@property({ type: String, attribute: false }) permalink;
	@property({ type: String, attribute: false }) question;
	@property({ type: Array, attribute: false }) tags = [];

    @state() contentLoaded = false;
    @state() iframeHeight = 0;

    // eslint-disable-next-line require-jsdoc
    render() {
        return html`
            <div 
                class="item"
                ?open=${ this.open }
            >
				<div 
                    class="item-summary"
                    @click=${ this.toggle }
                >
                    <div class="item-title">
                        ${ html`${ /* this.question is already escaped in the REST call */ unsafeHTML( this.question ) }` }

                        ${ repeat(
                            Object.entries( this.tags ),
                            // eslint-disable-next-line no-unused-vars
                            ( [ index, tagID ] ) => index,
                            // eslint-disable-next-line no-unused-vars
                            ( [ index, tagID ] ) => html`<lfm-faq-tag tag-id="${ tagID }"></lfm-faq-tag>`
                        ) }

                        ${ 
                            UOLFM_FAQ.currentUserCanEdit && isDefined( this.editURL ) ? 
                            html`
                                <a 
                                    href="${ this.editURL.replaceAll( '&#038;', '&' ) }" 
                                    target="_blank" 
                                    class="item-edit-link"
                                ></a>
                            ` : ''
                        }
                    </div>

                    <div class="item-toggle"></div>
                </div>

                ${ this.templateContent() }
			</div>
        `;
    }

    /**
	 * Toggle the item
	 */
	async toggle() {
		// Set the inverse value
		this.open = ! this.open;

        // If the user is closing the FAQ, we'll also be removing the iframe, which means next
        // time the user opens it, the content won't be loaded right away. For that reason,
        // before closing it we have to update the status of the property .contentLoaded
        this.contentLoaded = false;
        this.iframeHeight  = 0;

        // Wait for `open` state to be rendered to the DOM
        await this.updateComplete;

		// Dispatch de toggle event
		this.dispatchEvent(
			new CustomEvent( 'toggle', {
				bubbles: false, 
				composed: true
			} )
		);
	}

    /**
     * Loads and render the FAQ content
     * 
     * @returns TemplateResult
     */
    templateContent() {
        // Check if the content is opened
        if ( ! this.open ) {
            return;
        }

        return html`
            <div class="item-content">
                ${ this.templateLoadingPlaceholder }
                ${ this.templateIframe }
            </div>
        `;
    }

    /**
     * The content displayed while the FAQ content loads
     */
    get templateLoadingPlaceholder() {
        // If the content is already loaded, don't render the placeholder
        if ( this.contentLoaded ) {
            return;
        }

        // Create an array to save the length of each word
        const words = [];
        const numberOfWords = getRandomInteger( 40, 100 );

        // Create between 8 to 20 words per fake item
        for ( let j = 0; j < numberOfWords; j++ ) {
            // Get a random length
            words.push( getRandomInteger( 30, 150 ) );
        }

        return html`
            <div class="item-content-placeholder-body">
                ${ repeat(
                    // Get integrations with conditions
                    Object.entries( words ),
                    // eslint-disable-next-line no-unused-vars
                    ( [ index, words ] ) => index,
                    // eslint-disable-next-line no-unused-vars
                    ( [ index, wordLength ] ) => html`
                        <lfm-faq-placeholder width="${ wordLength }px"></lfm-faq-placeholder>
                    `
                ) }
            </div>

            <div class="item-content-placeholder-comments">
                <lfm-faq-placeholder width="40px"></lfm-faq-placeholder> <lfm-faq-placeholder width="150px"></lfm-faq-placeholder>
            </div>
        `;
    }

    /**
     * The iframe that contains the post content
     */
    get templateIframe() {
        return html`
            <iframe 
                src="${ addParametersToURL( this.permalink, { faq_context: 'archive' } ) }"

                @load=${ ( event ) => {
                    // The content loaded, update the protected property
                    this.contentLoaded = true;

                    // Set the size of the iframe
                    this.iframeHeight = event.target.contentWindow.document.body.scrollHeight;

                    // Create an observer instance to listen to resize changes in the main element of the page
                    const resizeObserver = new ResizeObserver( throttle( ( entries ) => {
                        this.iframeHeight = entries[0].target.clientHeight;
                    }, 100 ) );

                    // Start observing a DOM node
                    resizeObserver.observe( event.target.contentWindow.document.getElementById( 'uolfm-faq-single' ) );
                } }
                
                style="height: ${ this.iframeHeight }px"

                class="item-content-iframe ${ this.contentLoaded ? 'item-content-iframe--loaded' : '' }"
            ></iframe>
        `;
    }
}
